import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import AppLayout from './components/AppLayout';
import LoginPage from './pages/LoginPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import MyDayPage from './pages/MyDayPage';
import AdminHomePage from './pages/admin/AdminHomePage';
import CallsPage from './pages/CallsPage';
import CandidatesPage from './pages/CandidatesPage';
import AttendancePage from './pages/AttendancePage';
import ClientsPage from './pages/ClientsPage';
import ClientDetailPage from './pages/ClientDetailPage';
import InterviewsPage from './pages/InterviewsPage';
import JobsPage from './pages/JobsPage';
import OffersPage from './pages/OffersPage';
import PlacementsPage from './pages/PlacementsPage';
import TrackerPage from './pages/TrackerPage';
import LeavePage from './pages/LeavePage';
import AttendanceRecordsPage from './pages/AttendanceRecordsPage';
import TeamPage from './pages/TeamPage';
import TimesheetsPage from './pages/TimesheetsPage';
import MyProfilePage from './pages/MyProfilePage';
import HelpPage from './pages/HelpPage';
import PayslipsPage from './pages/PayslipsPage';
import BillingPage from './pages/admin/BillingPage';
import CompaniesPage from './pages/admin/CompaniesPage';
import AuditLogPage from './pages/admin/AuditLogPage';
import ExportsPage from './pages/admin/ExportsPage';
import CustomFieldsPage from './pages/admin/CustomFieldsPage';
import RetiredCandidatesPage from './pages/admin/RetiredCandidatesPage';
import InvoicesPage from './pages/admin/InvoicesPage';
import OverviewPage from './pages/admin/OverviewPage';
import PaySetupPage from './pages/admin/PaySetupPage';
import PayrollPage from './pages/admin/PayrollPage';
import UsersPage from './pages/admin/UsersPage';
import ProfileReviewsPage from './pages/admin/ProfileReviewsPage';
import EmployeeDetailPage from './pages/admin/EmployeeDetailPage';
import WorkplacesPage from './pages/admin/WorkplacesPage';
import {
  canSeeAllCompanies,
  isCompanyAdmin,
  isOwner,
  isStaff,
  isSupervisor,
  useAuth,
} from './store/auth';
import { useImpersonation } from './store/impersonation';
import { hasCompanyChoice } from './lib/companyScope';

/**
 * A company-scoped page, shown only once the owner has said which company.
 *
 * Everyone else falls straight through: they belong to exactly one company and
 * have never seen the picker.
 *
 * An owner belongs to none, and until this existed "no company chosen" was
 * sent to the API as no `company_id` at all -- which 33 list routes read as
 * every company. Nobody chose that. It was what happened when nobody chose
 * anything, and it is how the Daily Calls Report came to open with 35,032
 * candidates drawn from four tenants at once.
 *
 * A wrapper rather than a check inside each page, because a page that has
 * mounted has already fired its queries. Refusing to render it is what stops
 * the requests, and it puts the rule in one place instead of thirty.
 *
 * "All companies" passes. It is a choice, and reading across tenants was never
 * the problem -- doing it without having asked was.
 *
 * Not applied while viewing as somebody else: the requests go out under their
 * token, so the company is theirs and the picker is hidden.
 */
function RequireCompany({ children }: { children: React.ReactNode }) {
  const user = useAuth((s) => s.user);
  const actingCompanyId = useAuth((s) => s.actingCompanyId);
  const viewing = useImpersonation((s) => s.target);

  if (!canSeeAllCompanies(user) || viewing || hasCompanyChoice(actingCompanyId)) {
    return <>{children}</>;
  }

  return (
    <div className="card text-center">
      <h2 className="text-base font-semibold text-gray-900">
        Choose a Company
      </h2>
      <p className="mx-auto mt-1 max-w-md text-sm text-gray-500">
        This page shows one company's records, and you work across several.
        Pick one in the bar above and it will load.
      </p>
      <p className="mx-auto mt-2 max-w-md text-xs text-gray-400">
        Choose <span className="font-medium text-gray-500">All companies</span>
        {' '}to look across every one at once — useful for reading, though
        nothing can be created there.
      </p>
    </div>
  );
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuth((s) => s.isAuthenticated);
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return <>{children}</>;
}

function RequireCompanyAdmin({ children }: { children: React.ReactNode }) {
  const user = useAuth((s) => s.user);
  if (!isCompanyAdmin(user)) {
    return (
      <div className="card text-center">
        <h2 className="text-base font-semibold text-gray-900">Not Available</h2>
        <p className="mt-1 text-sm text-gray-500">
          This page is limited to company administrators and the owner.
        </p>
      </div>
    );
  }
  return <>{children}</>;
}

/**
 * Recruitment screens, closed to an employee.
 *
 * The server refuses these routes outright, so without this an employee who
 * typed the URL would get a page that renders and then fails every request on
 * it — a screen of error toasts rather than an answer. Saying so plainly is
 * the same courtesy the other two guards already extend.
 */
function RequireOwner({ children }: { children: React.ReactNode }) {
  const user = useAuth((s) => s.user);
  if (!isOwner(user)) {
    return (
      <div className="card text-center">
        <h2 className="text-base font-semibold text-gray-900">Not Available</h2>
        <p className="mt-1 text-sm text-gray-500">
          This page is limited to the owner.
        </p>
      </div>
    );
  }
  return <>{children}</>;
}

function RequireStaff({ children }: { children: React.ReactNode }) {
  const user = useAuth((s) => s.user);
  if (!isStaff(user)) {
    return (
      <div className="card text-center">
        <h2 className="text-base font-semibold text-gray-900">Not Available</h2>
        <p className="mt-1 text-sm text-gray-500">
          This page is part of recruitment. Your day, attendance, leave,
          timesheets and payslips are all on the bar above.
        </p>
      </div>
    );
  }
  return <>{children}</>;
}

/**
 * Where the brand in the top-left lands, which is not one place.
 *
 * My Day is a personal screen: today's shift, this person's follow-ups, the
 * candidates they promised to ring. That is the right home for everybody who
 * has a day of their own to work.
 *
 * An administrator does not, or not principally. They have a company to read
 * rather than a worklist to clear, and an owner has several — which is why My
 * Day is `hideFromOwner` in the navigation and why an owner used to be sent to
 * the Daily Calls Report from here. /admin/home is what that redirect was
 * reaching for: the same intent, aimed at a screen built for it.
 *
 * Company admins move too, not only owners. They were landing on My Day, which
 * for somebody running a desk is half a screen of their own follow-ups and no
 * view of the company at all. My Day stays in their navigation, so the screen
 * is one click away rather than gone.
 *
 * **Redirects, not rendered elements, and `/` renders nothing itself.** Both
 * destinations are real addresses, so somebody can link a colleague to what
 * they were looking at, and neither page has two URLs. That mattered enough to
 * move My Day off `/` — a page reachable at both `/` and `/my-day` would have
 * a navigation tab that never lights up on one of them.
 */
function HomeRoute() {
  const user = useAuth((s) => s.user);

  if (isCompanyAdmin(user)) return <Navigate to="/admin/home" replace />;
  return <Navigate to="/my-day" replace />;
}

function RequireSupervisor({ children }: { children: React.ReactNode }) {
  const user = useAuth((s) => s.user);
  if (!isSupervisor(user)) {
    return (
      <div className="card text-center">
        <h2 className="text-base font-semibold text-gray-900">Not Available</h2>
        <p className="mt-1 text-sm text-gray-500">
          This page is limited to managers and above.
        </p>
      </div>
    );
  }
  return <>{children}</>;
}

export default function App() {
  const isAuthenticated = useAuth((s) => s.isAuthenticated);
  const refreshUser = useAuth((s) => s.refreshUser);

  // Re-read the user on load so a role change or deactivation applies
  // immediately rather than at the next login.
  useEffect(() => {
    if (isAuthenticated) void refreshUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* Public by necessity: whoever follows a reset link cannot sign in,
          which is the whole reason the link exists. */}
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route
        element={
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        }
      >
        {/* `/` decides where you belong and sends you there; the two
            destinations below are the real pages. */}
        <Route index element={<HomeRoute />} />
        <Route path="my-day" element={<MyDayPage />} />
        {/* Company admin and owner. Guarded rather than merely unlinked: the
            figures span a whole company, and a recruiter typing the address
            should meet the same refusal the other /admin screens give. */}
        <Route
          path="admin/home"
          element={
            <RequireCompanyAdmin>
              <AdminHomePage />
            </RequireCompanyAdmin>
          }
        />
        <Route
          path="calls"
          element={
            <RequireStaff>
              <RequireCompany>
                <CallsPage />
              </RequireCompany>
            </RequireStaff>
          }
        />
        <Route
          path="candidates"
          element={
            <RequireStaff>
              <RequireCompany>
                <CandidatesPage />
              </RequireCompany>
            </RequireStaff>
          }
        />
        <Route path="attendance" element={<AttendancePage />} />
        {/* Anyone signed in can file their own leave and read the holiday
            list; approving and editing holidays gate themselves inside. */}
        <Route path="leave" element={<LeavePage />} />
        <Route
          path="clients"
          element={
            <RequireStaff>
              <RequireCompany>
                <ClientsPage />
              </RequireCompany>
            </RequireStaff>
          }
        />
        <Route
          path="clients/:clientId"
          element={
            <RequireStaff>
              <RequireCompany>
                <ClientDetailPage />
              </RequireCompany>
            </RequireStaff>
          }
        />
        {/* The recruiter's own pipeline, so no role guard beyond signing in —
            the server scopes it to whatever the viewer owns. */}
        <Route
          path="interviews"
          element={
            <RequireStaff>
              <RequireCompany>
                <InterviewsPage />
              </RequireCompany>
            </RequireStaff>
          }
        />
        {/* Postings are company-level; the API scopes what each role sees. */}
        <Route
          path="jobs"
          element={
            <RequireStaff>
              <RequireCompany>
                <JobsPage />
              </RequireCompany>
            </RequireStaff>
          }
        />
        <Route
          path="offers"
          element={
            <RequireStaff>
              <RequireCompany>
                <OffersPage />
              </RequireCompany>
            </RequireStaff>
          }
        />
        {/* Onboarding gates itself inside — the server requires an approver. */}
        <Route
          path="placements"
          element={
            <RequireStaff>
              <RequireCompany>
                <PlacementsPage />
              </RequireCompany>
            </RequireStaff>
          }
        />
        {/* A report over the same placements, in the tracker sheet's
            column order. No role guard: the server scopes the list, so a
            recruiter opening it sees their own rows and nobody else's. */}
        <Route
          path="tracker"
          element={
            <RequireStaff>
              <RequireCompany>
                <TrackerPage />
              </RequireCompany>
            </RequireStaff>
          }
        />
        {/* Anyone signed in files their own week; approving gates itself
            inside, and the server scopes the list to what the viewer owns. */}
        <Route path="timesheets" element={<TimesheetsPage />} />
        <Route path="payslips" element={<PayslipsPage />} />
        {/* No guard: everybody has a profile, and the route answers
            for itself when no employee record is linked. */}
        <Route path="profile" element={<MyProfilePage />} />
        {/* No guard beyond signing in: every role has something to read
            here, and the page gates its own chapters rather than refusing
            the whole screen to somebody who is lost. */}
        <Route path="help" element={<HelpPage />} />
        {/* Owner only, and refused by the server too — the worklist behind
            it is SuperAdmin. /admin/billing is untouched and still serves
            managers and admins. */}
        <Route
          path="admin/invoices"
          element={
            <RequireOwner>
              <RequireCompany>
                <InvoicesPage />
              </RequireCompany>
            </RequireOwner>
          }
        />
        <Route
          path="admin/retired-candidates"
          element={
            <RequireCompanyAdmin>
              <RequireCompany>
                <RetiredCandidatesPage />
              </RequireCompany>
            </RequireCompanyAdmin>
          }
        />
        <Route
          path="admin/custom-fields"
          element={
            <RequireOwner>
              <RequireCompany>
                <CustomFieldsPage />
              </RequireCompany>
            </RequireOwner>
          }
        />
        <Route
          path="team"
          element={
            <RequireSupervisor>
              <RequireCompany>
                <TeamPage />
              </RequireCompany>
            </RequireSupervisor>
          }
        />
        {/* Same guard as Team, and the server applies it again: the roster it
            reads is company-scoped for a manager and picker-scoped for the
            owner. */}
        <Route
          path="attendance-records"
          element={
            <RequireSupervisor>
              <RequireCompany>
                <AttendanceRecordsPage />
              </RequireCompany>
            </RequireSupervisor>
          }
        />
        <Route
          path="admin/overview"
          element={
            <RequireCompanyAdmin>
              <RequireCompany>
                <OverviewPage />
              </RequireCompany>
            </RequireCompanyAdmin>
          }
        />
        <Route
          path="admin/payroll"
          element={
            <RequireCompanyAdmin>
              <RequireCompany>
                <PayrollPage />
              </RequireCompany>
            </RequireCompanyAdmin>
          }
        />
        <Route
          path="admin/pay-setup"
          element={
            <RequireCompanyAdmin>
              <RequireCompany>
                <PaySetupPage />
              </RequireCompany>
            </RequireCompanyAdmin>
          }
        />
        {/* Managers may read billing — chasing payment is their job — so this
            is supervisor-gated, and the write controls inside gate themselves
            on company_admin to match what the API allows. */}
        <Route
          path="admin/billing"
          element={
            <RequireSupervisor>
              <BillingPage />
            </RequireSupervisor>
          }
        />
        <Route
          path="admin/companies"
          element={
            <RequireSupervisor>
              <CompaniesPage />
            </RequireSupervisor>
          }
        />
        {/* Approver-only server-side: bulk extraction is a different act from
            reading the same rows on screen. The page re-checks inside too. */}
        {/* Company-admin, matching the API: this is role changes and
            password resets, not the approvals a manager already sees. */}
        <Route
          path="admin/audit-logs"
          element={
            <RequireCompanyAdmin>
              <AuditLogPage />
            </RequireCompanyAdmin>
          }
        />
        <Route
          path="admin/exports"
          element={
            <RequireSupervisor>
              <RequireCompany>
                <ExportsPage />
              </RequireCompany>
            </RequireSupervisor>
          }
        />
        <Route
          path="admin/users"
          element={
            <RequireSupervisor>
              <RequireCompany>
                <UsersPage />
              </RequireCompany>
            </RequireSupervisor>
          }
        />
        {/* The full employee record — bank, PAN, Aadhaar and every document.
            Company-admin and owner only, matching the audited API behind it. */}
        <Route
          path="admin/users/:userId"
          element={
            <RequireCompanyAdmin>
              <RequireCompany>
                <EmployeeDetailPage />
              </RequireCompany>
            </RequireCompanyAdmin>
          }
        />
        <Route
          path="admin/workplaces"
          element={
            <RequireSupervisor>
              <RequireCompany>
                <WorkplacesPage />
              </RequireCompany>
            </RequireSupervisor>
          }
        />
        {/* Company-admin and owner: this screen shows full bank, PAN and
            Aadhaar, and the API gates it the same way. */}
        <Route
          path="admin/profile-reviews"
          element={
            <RequireCompanyAdmin>
              <RequireCompany>
                <ProfileReviewsPage />
              </RequireCompany>
            </RequireCompanyAdmin>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
